import { createTheme } from '@mui/material/styles';

// Custom breakpoints for better responsive design
const breakpoints = {
  values: {
    xs: 0,
    sm: 600,
    md: 960,
    lg: 1280,
    xl: 1920,
    mobile: 600,
    tablet: 960,
    desktop: 1280,
  },
};

// Gold gradient definitions
const goldGradient = 'linear-gradient(135deg, #C8A96B 0%, #E8D5A8 50%, #C8A96B 100%)';
const darkGoldGradient = 'linear-gradient(135deg, #A88A4E 0%, #C8A96B 100%)';
const subtleGoldGradient = 'linear-gradient(135deg, rgba(200,169,107,0.1) 0%, rgba(200,169,107,0.05) 100%)';

export const theme = createTheme({
  breakpoints,
  
  palette: {
    mode: 'dark',
    primary: {
      main: '#C8A96B',
      light: '#D6B57B',
      dark: '#A88A4E',
      contrastText: '#000000',
      gradient: goldGradient,
    },
    secondary: {
      main: '#0B0B0B',
      light: '#1A1A1A',
      dark: '#050505',
      contrastText: '#C8A96B',
    },
    background: {
      default: '#0B0B0B',
      paper: '#111111',
      elevated: '#1A1A1A',
      dark: '#050505',
    },
    text: {
      primary: '#FFFFFF',
      secondary: '#CCCCCC',
      disabled: '#666666',
      gold: '#C8A96B',
      darkGold: '#A88A4E',
    },
    gold: {
      main: '#C8A96B',
      light: '#E8D5A8',
      dark: '#9E7E3E',
      gradient: goldGradient,
    },
    error: {
      main: '#CF6679',
    },
    warning: {
      main: '#F0A500',
    },
    info: {
      main: '#2196F3',
    },
    success: {
      main: '#4CAF50',
    },
    divider: 'rgba(200,169,107,0.15)',
  },
  
  typography: {
    fontFamily: '"Inter", "Playfair Display", "Helvetica Neue", "Segoe UI", "Roboto", sans-serif',
    
    h1: {
      fontFamily: '"Playfair Display", serif',
      fontWeight: 800,
      letterSpacing: '-0.02em',
      lineHeight: 1.1,
      fontSize: '4rem',
      '@media (max-width:960px)': {
        fontSize: '3rem',
      },
      '@media (max-width:600px)': {
        fontSize: '2.5rem',
      },
    },
    
    h2: {
      fontFamily: '"Playfair Display", serif',
      fontWeight: 700,
      letterSpacing: '-0.01em',
      lineHeight: 1.2,
      fontSize: '3rem',
      '@media (max-width:960px)': {
        fontSize: '2.5rem',
      },
      '@media (max-width:600px)': {
        fontSize: '2rem',
      },
    },
    
    h3: {
      fontFamily: '"Playfair Display", serif',
      fontWeight: 700,
      lineHeight: 1.3,
      fontSize: '2.5rem',
      '@media (max-width:960px)': {
        fontSize: '2rem',
      },
      '@media (max-width:600px)': {
        fontSize: '1.75rem',
      },
    },
    
    h4: {
      fontFamily: '"Playfair Display", serif',
      fontWeight: 600,
      lineHeight: 1.4,
      fontSize: '2rem',
      '@media (max-width:960px)': {
        fontSize: '1.75rem',
      },
      '@media (max-width:600px)': {
        fontSize: '1.5rem',
      },
    },
    
    h5: {
      fontFamily: '"Playfair Display", serif',
      fontWeight: 600,
      lineHeight: 1.4,
      fontSize: '1.5rem',
      '@media (max-width:960px)': {
        fontSize: '1.35rem',
      },
    },
    
    h6: {
      fontFamily: '"Playfair Display", serif',
      fontWeight: 600,
      lineHeight: 1.5,
      fontSize: '1.25rem',
    },
    
    subtitle1: {
      fontFamily: '"Inter", sans-serif',
      letterSpacing: '0.02em',
      fontSize: '1rem',
      fontWeight: 500,
    },
    
    subtitle2: {
      fontFamily: '"Inter", sans-serif',
      letterSpacing: '0.01em',
      fontSize: '0.875rem',
      fontWeight: 500,
    },
    
    body1: {
      fontFamily: '"Inter", sans-serif',
      lineHeight: 1.8,
      fontSize: '1rem',
      '@media (max-width:600px)': {
        fontSize: '0.9375rem',
      },
    },
    
    body2: {
      fontFamily: '"Inter", sans-serif',
      lineHeight: 1.6,
      fontSize: '0.875rem',
    },
    
    button: {
      fontFamily: '"Inter", sans-serif',
      fontWeight: 600,
      textTransform: 'none',
      letterSpacing: '0.02em',
    },
    
    caption: {
      fontFamily: '"Inter", sans-serif',
      fontSize: '0.75rem',
      letterSpacing: '0.02em',
    },
    
    overline: {
      fontFamily: '"Inter", sans-serif',
      fontSize: '0.7rem',
      fontWeight: 600,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
    },
  },
  
  shape: {
    borderRadius: 8,
    borderRadiusSmall: 4,
    borderRadiusLarge: 12,
    borderRadiusXLarge: 16,
  },
  
  shadows: [
    'none',
    '0px 2px 4px rgba(0,0,0,0.1)',
    '0px 4px 8px rgba(0,0,0,0.15)',
    '0px 8px 16px rgba(0,0,0,0.2)',
    '0px 12px 24px rgba(0,0,0,0.25)',
    '0px 16px 32px rgba(0,0,0,0.3)',
    '0px 20px 40px rgba(0,0,0,0.35)',
    '0px 24px 48px rgba(0,0,0,0.4)',
    '0px 28px 56px rgba(0,0,0,0.45)',
    '0px 32px 64px rgba(0,0,0,0.5)',
    '0px 36px 72px rgba(0,0,0,0.55)',
    '0px 40px 80px rgba(0,0,0,0.6)',
    ...Array(13).fill('none'),
  ],
  
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          margin: 0,
          padding: 0,
          boxSizing: 'border-box',
        },
        html: {
          scrollBehavior: 'smooth',
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        },
        body: {
          overflowX: 'hidden',
          background: '#0B0B0B',
        },
        '::-webkit-scrollbar': {
          width: '10px',
          height: '10px',
        },
        '::-webkit-scrollbar-track': {
          background: '#111',
          borderRadius: '10px',
        },
        '::-webkit-scrollbar-thumb': {
          background: '#C8A96B',
          borderRadius: '10px',
          transition: 'all 0.3s',
        },
        '::-webkit-scrollbar-thumb:hover': {
          background: '#D6B57B',
        },
        '@keyframes shimmer': {
          '0%': {
            backgroundPosition: '-1000px 0',
          },
          '100%': {
            backgroundPosition: '1000px 0',
          },
        },
        '@keyframes fadeInUp': {
          '0%': {
            opacity: 0,
            transform: 'translateY(30px)',
          },
          '100%': {
            opacity: 1,
            transform: 'translateY(0)',
          },
        },
        '@keyframes fadeIn': {
          '0%': { opacity: 0 },
          '100%': { opacity: 1 },
        },
        '@keyframes slideInRight': {
          '0%': {
            opacity: 0,
            transform: 'translateX(-30px)',
          },
          '100%': {
            opacity: 1,
            transform: 'translateX(0)',
          },
        },
        '@keyframes pulse': {
          '0%, 100%': {
            opacity: 1,
          },
          '50%': {
            opacity: 0.5,
          },
        },
        '@keyframes rotate': {
          '0%': {
            transform: 'rotate(0deg)',
          },
          '100%': {
            transform: 'rotate(360deg)',
          },
        },
      },
    },
    
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          padding: '10px 24px',
          fontWeight: 600,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
        },
        containedPrimary: {
          background: goldGradient,
          color: '#000000',
          '&:hover': {
            background: darkGoldGradient,
            boxShadow: '0 8px 20px rgba(200,169,107,0.3)',
          },
        },
        outlinedPrimary: {
          borderColor: '#C8A96B',
          borderWidth: '2px',
          '&:hover': {
            borderColor: '#D6B57B',
            background: 'rgba(200,169,107,0.1)',
          },
        },
        sizeLarge: {
          padding: '12px 32px',
          fontSize: '1.1rem',
        },
        sizeSmall: {
          padding: '6px 16px',
          fontSize: '0.875rem',
        },
      },
    },
    
    MuiCard: {
      styleOverrides: {
        root: {
          background: '#111111',
          borderRadius: '16px',
          transition: 'all 0.3s ease-in-out',
          overflow: 'hidden',
          '&:hover': {
            transform: 'translateY(-5px)',
            boxShadow: '0 20px 40px rgba(0,0,0,0.5)',
          },
        },
      },
    },
    
    MuiCardMedia: {
      styleOverrides: {
        root: {
          transition: 'transform 0.6s ease-in-out',
          '&:hover': {
            transform: 'scale(1.05)',
          },
        },
      },
    },
    
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        elevation1: {
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
        },
        elevation2: {
          boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
        },
        elevation3: {
          boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
        },
      },
    },
    
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'rgba(11,11,11,0.8)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(200,169,107,0.1)',
        },
      },
    },
    
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            transition: 'all 0.3s',
            '& fieldset': {
              borderColor: 'rgba(200,169,107,0.3)',
            },
            '&:hover fieldset': {
              borderColor: '#C8A96B',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#C8A96B',
              borderWidth: '2px',
            },
          },
          '& .MuiInputLabel-root': {
            color: '#999',
            '&.Mui-focused': {
              color: '#C8A96B',
            },
          },
        },
      },
    },
    
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(200,169,107,0.15)',
        },
      },
    },
    
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '4px',
          fontWeight: 600,
        },
        colorPrimary: {
          background: goldGradient,
          color: '#000000',
        },
        outlined: {
          borderColor: '#C8A96B',
          color: '#C8A96B',
        },
      },
    },
    
    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: 'all 0.3s',
          '&:hover': {
            transform: 'scale(1.1)',
          },
        },
      },
    },
    
    MuiContainer: {
      styleOverrides: {
        root: {
          '@media (min-width: 1280px)': {
            maxWidth: '1200px',
          },
        },
      },
    },
    
    MuiDialog: {
      styleOverrides: {
        paper: {
          background: '#111111',
          borderRadius: '16px',
          border: '1px solid rgba(200,169,107,0.2)',
        },
      },
    },
    
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          background: '#1A1A1A',
          color: '#C8A96B',
          border: '1px solid rgba(200,169,107,0.3)',
          fontSize: '0.75rem',
          padding: '8px 12px',
        },
        arrow: {
          color: '#1A1A1A',
        },
      },
    },
    
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          background: 'rgba(200,169,107,0.1)',
          borderRadius: '4px',
        },
        bar: {
          background: goldGradient,
          borderRadius: '4px',
        },
      },
    },
    
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '1rem',
          '&.Mui-selected': {
            color: '#C8A96B',
          },
        },
      },
    },
    
    MuiTabs: {
      styleOverrides: {
        indicator: {
          background: goldGradient,
          height: '3px',
        },
      },
    },
    
    MuiList: {
      styleOverrides: {
        root: {
          '& .MuiListItemButton-root:hover': {
            background: 'rgba(200,169,107,0.1)',
          },
        },
      },
    },
    
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&:hover': {
            background: 'rgba(200,169,107,0.1)',
          },
        },
      },
    },
    
    MuiSkeleton: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(90deg, #1A1A1A 25%, #2A2A2A 50%, #1A1A1A 75%)',
          backgroundSize: '1000px 100%',
          animation: 'shimmer 2s infinite',
        },
      },
    },
    
    MuiSnackbar: {
      styleOverrides: {
        root: {
          '& .MuiAlert-root': {
            background: '#1A1A1A',
            color: '#C8A96B',
            border: '1px solid rgba(200,169,107,0.3)',
          },
        },
      },
    },
  },
});

// Custom utility classes for gold gradient text
export const globalStyles = {
  goldText: {
    background: goldGradient,
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    color: 'transparent',
  },
  goldBorder: {
    border: '1px solid #C8A96B',
  },
  goldShadow: {
    boxShadow: '0 0 20px rgba(200,169,107,0.3)',
  },
  glassEffect: {
    background: 'rgba(255,255,255,0.05)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(200,169,107,0.2)',
  },
};

export default theme;